/**
 * F-133: the BGM stems tag — the whole of what a board says about its music
 * now that scoring is optional work running alongside the shoot.
 *
 * Two states, and they are the two the tag exists to tell apart at a glance
 * across a column of cards: STILL SCORING (a turning ring) and DONE (a green
 * check). Done means every mood run carries `BGM_MIN_STEMS` stems (F-139) —
 * the board's own coverage rule (`bgmStemsDone`), not a status anyone sets.
 *
 * The count rides along while it is unfinished, because "3 of 8" is the
 * question the producer actually has; once it is done the number is noise and
 * the check says everything.
 *
 * A script with no moods yet has nothing to count, so it shows the ring with
 * no fraction rather than a confident "0/0" — and the ring does NOT turn,
 * because nothing is under way. An animation is a claim about activity, and a
 * board nobody has divided is not being scored.
 */
import { BGM_MIN_STEMS, bgmStemsDone, type BgmStemsSummary } from "@kola/shared";

export interface BgmStemsTagProps {
  stems: BgmStemsSummary;
  /** Extra classes for a caller that places the tag itself (the board chip). */
  className?: string;
}

export function BgmStemsTag({ stems, className }: BgmStemsTagProps) {
  const done = bgmStemsDone(stems);
  const idle = !done && stems.total === 0;
  const counted = stems.total > 0 ? `${stems.covered}/${stems.total}` : null;
  const title = done
    ? `BGM stems: every mood has ${BGM_MIN_STEMS}+ stems (${stems.total})`
    : counted
      ? `BGM stems: ${stems.covered} of ${stems.total} moods have ${BGM_MIN_STEMS}+ stems`
      : "BGM stems: no moods to score yet";

  return (
    <span
      className={`bgm-tag${done ? " bgm-tag-done" : idle ? "" : " bgm-tag-ongoing"}${
        className ? ` ${className}` : ""
      }`}
      title={title}
    >
      {done ? (
        <span className="bgm-tag-icon" aria-hidden>
          ✓
        </span>
      ) : (
        <span className={`bgm-tag-spinner${idle ? " bgm-tag-spinner-idle" : ""}`} aria-hidden />
      )}
      <span className="bgm-tag-label">BGM stems</span>
      {!done && counted !== null && <span className="bgm-tag-count">{counted}</span>}
    </span>
  );
}
